import { useMemo, useState } from 'react';
import { useConfig } from '@/core/config/ConfigProvider';
import { Input, cx, describedBy } from '@/design-system';
import type { FieldComponentProps } from '../field-registry';

/**
 * CAMPO DE SENHA
 *
 * O medidor não é enfeite: dizer "fraca" depois do envio ensina pouco, e
 * dizer enquanto a pessoa digita muda o comportamento dela. O que ele mede é
 * o que a política da configuração exige, mais comprimento — deliberadamente
 * simples, porque medidor complexo dá falsa sensação de rigor.
 *
 * O botão de revelar existe porque esconder a senha em campo de CADASTRO
 * aumenta erro de digitação sem ganho real de segurança: quem está sozinho no
 * navegador não tem por que digitar às cegas.
 */

export type PasswordStrength = { score: 0 | 1 | 2 | 3; label: string; hints: string[] };

export function measurePassword(
  value: string,
  policy: { minLength: number; requiresMixedCase: boolean; requiresNumber: boolean },
): PasswordStrength {
  const hints: string[] = [];

  if (value.length < policy.minLength) hints.push(`ao menos ${policy.minLength} caracteres`);
  if (policy.requiresMixedCase && !(/[a-z]/.test(value) && /[A-Z]/.test(value))) hints.push('maiúscula e minúscula');
  if (policy.requiresNumber && !/\d/.test(value)) hints.push('um número');

  if (value.length === 0) return { score: 0, label: '', hints };
  if (hints.length > 0) return { score: 1, label: 'Fraca', hints };

  // Passou na política. O que separa "boa" de "forte" é comprimento e
  // variedade — os dois fatores que realmente encarecem um ataque.
  const longEnough = value.length >= policy.minLength + 4;
  const hasSymbol = /[^A-Za-z0-9]/.test(value);

  if (longEnough && hasSymbol) return { score: 3, label: 'Forte', hints: [] };
  return { score: 2, label: 'Boa', hints: [] };
}

const METER_TONE = ['bg-line', 'bg-danger', 'bg-warning', 'bg-positive'] as const;

function EyeIcon({ open }: { open: boolean }) {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      {open ? (
        <>
          <path d="M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12Z" strokeLinejoin="round" />
          <circle cx="12" cy="12" r="2.75" />
        </>
      ) : (
        <>
          <path d="M4 4l16 16" strokeLinecap="round" />
          <path d="M9.9 5.7A10.6 10.6 0 0 1 12 5.5c6.4 0 10 6.5 10 6.5a17 17 0 0 1-3.3 4M6.3 7.9A16.7 16.7 0 0 0 2 12s3.6 6.5 10 6.5c1.2 0 2.3-.2 3.3-.6" strokeLinecap="round" />
        </>
      )}
    </svg>
  );
}

export function PasswordControl({
  field,
  value,
  onChange,
  onBlur,
  error,
  disabled,
  inputId,
}: FieldComponentProps) {
  const [revealed, setRevealed] = useState(false);
  const text = typeof value === 'string' ? value : '';

  // Um campo de CONFIRMAÇÃO não mostra medidor: a força já foi avaliada no
  // campo original, e repetir a barra só polui.
  const isConfirmation = field.validation.matchesField !== null;

  // A política é configuração (Cap. 2.1): mudar o tamanho mínimo da senha é
  // edição no painel, e o medidor acompanha sem alteração de código.
  const { account } = useConfig().rules;

  const strength = useMemo(
    () =>
      measurePassword(text, {
        minLength: field.validation.minLength ?? account.passwordMinLength,
        requiresMixedCase: account.passwordRequiresMixedCase,
        requiresNumber: account.passwordRequiresNumber,
      }),
    [text, field.validation.minLength, account],
  );

  return (
    <div className="flex flex-col gap-2">
      <div className="relative">
        <Input
          id={inputId}
          type={revealed ? 'text' : 'password'}
          value={text}
          onChange={(event) => onChange(event.target.value)}
          onBlur={onBlur}
          disabled={disabled}
          className="pr-12"
          placeholder={field.presentation.placeholder ?? undefined}
          autoComplete={isConfirmation ? 'new-password' : field.key.includes('nova') ? 'new-password' : 'current-password'}
          aria-invalid={Boolean(error)}
          aria-describedby={describedBy(inputId, field.presentation.help, error)}
        />

        <button
          type="button"
          onClick={() => setRevealed((current) => !current)}
          className="absolute inset-y-0 right-0 grid w-11 place-items-center text-ink-faint hover:text-ink"
          // O rótulo muda com o estado: um leitor de tela precisa saber o que
          // o botão FARÁ, não em que estado ele está.
          aria-label={revealed ? 'Ocultar senha' : 'Mostrar senha'}
          tabIndex={-1}
        >
          <EyeIcon open={revealed} />
        </button>
      </div>

      {!isConfirmation && text.length > 0 ? (
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-2">
            <div className="flex h-1.5 flex-1 gap-1" aria-hidden="true">
              {[1, 2, 3].map((level) => (
                <span
                  key={level}
                  className={cx('h-full flex-1 rounded-full transition-colors', level <= strength.score ? METER_TONE[strength.score] : 'bg-line')}
                />
              ))}
            </div>
            <span className="w-12 text-right text-xs font-semibold text-ink-soft">{strength.label}</span>
          </div>

          {/* aria-live: quem não vê a barra precisa ouvir o que ainda falta. */}
          <p className="text-xs text-ink-faint" aria-live="polite">
            {strength.hints.length > 0 ? `Falta: ${strength.hints.join(', ')}.` : 'Senha aceita.'}
          </p>
        </div>
      ) : null}
    </div>
  );
}
